import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ArrowLeft, ArrowRight, CheckCircle, TrendingUp, DollarSign, Target } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface VentureOnboardingProps {
  onComplete: (data: VentureFormData) => void;
  onBack: () => void;
  isCompletion?: boolean;
}

interface VentureFormData {
  firmName: string;
  aum: string;
  investmentFocus: string[];
  stagePreference: string[];
  ticketSizeMin: string;
  ticketSizeMax: string;
  portfolioCompanies: string;
  investmentCriteria: string;
  contactInfo: {
    email: string;
    phone: string;
    website: string;
  };
}

const initialFormData: VentureFormData = {
  firmName: '',
  aum: '',
  investmentFocus: [],
  stagePreference: [],
  ticketSizeMin: '',
  ticketSizeMax: '',
  portfolioCompanies: '',
  investmentCriteria: '',
  contactInfo: {
    email: '',
    phone: '',
    website: '',
  },
};

const focusAreas = [
  'Sports Technology', 'Athlete Performance', 'Fan Engagement', 'Sports Media',
  'Health & Fitness', 'Sports Analytics', 'Event Management', 'Sports Equipment',
  'Gaming & Esports', 'Sports Betting', 'Nutrition', 'Recovery Technology'
];

const stageOptions = [
  'Pre-Seed', 'Seed', 'Series A', 'Series B', 'Series C+', 'Growth', 'Late Stage'
];

const aumRanges = [
  'Under $10M', '$10M-$50M', '$50M-$100M', '$100M-$500M', '$500M-$1B', '$1B+'
];

const steps = [
  { id: 1, title: 'Firm Info', icon: TrendingUp },
  { id: 2, title: 'Investment Details', icon: DollarSign },
  { id: 3, title: 'Portfolio & Strategy', icon: Target },
  { id: 4, title: 'Review', icon: CheckCircle }
];

export const VentureOnboarding: React.FC<VentureOnboardingProps> = ({
  onComplete,
  onBack,
  isCompletion = false
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<VentureFormData>(initialFormData);
  const [newFocus, setNewFocus] = useState('');
  const [newStage, setNewStage] = useState('');
  const [loading, setLoading] = useState(false);

  const progress = (currentStep / steps.length) * 100;

  const updateFormData = (field: string, value: any) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...(prev[parent as keyof VentureFormData] as any),
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const addFocus = () => {
    if (newFocus && !formData.investmentFocus.includes(newFocus)) {
      updateFormData('investmentFocus', [...formData.investmentFocus, newFocus]);
      setNewFocus('');
    }
  };

  const removeFocus = (index: number) => {
    updateFormData('investmentFocus', formData.investmentFocus.filter((_, i) => i !== index));
  };

  const addStage = () => {
    if (newStage && !formData.stagePreference.includes(newStage)) {
      updateFormData('stagePreference', [...formData.stagePreference, newStage]);
      setNewStage('');
    }
  };

  const removeStage = (index: number) => {
    updateFormData('stagePreference', formData.stagePreference.filter((_, i) => i !== index));
  };

  const nextStep = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('Please sign in first');
        return;
      }

      // Get or create profile
      let { data: profile } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (!profile) {
        const { data: newProfile, error: profileError } = await supabase
          .from('profiles')
          .insert({
            user_id: user.id,
            email: user.email || '',
            display_name: formData.firmName || user.email?.split('@')[0] || 'VC',
            account_type: 'venture',
            setup_completed: true
          })
          .select('id')
          .single();

        if (profileError) {
          console.error('Error creating profile:', profileError);
          toast.error('Failed to create profile. Please try again.');
          return;
        }
        profile = newProfile;
      }

      // Prepare venture data - allow empty values
      const ventureData = {
        profile_id: profile.id,
        firm_name: formData.firmName || 'VC Firm',
        aum: formData.aum ? parseFloat(formData.aum.replace(/[^0-9.]/g, '')) || 0 : 0,
        investment_focus: formData.investmentFocus.length > 0 ? formData.investmentFocus : [],
        stage_preference: formData.stagePreference.length > 0 ? formData.stagePreference : [],
        ticket_size_min: formData.ticketSizeMin ? parseFloat(formData.ticketSizeMin) || 0 : 0,
        ticket_size_max: formData.ticketSizeMax ? parseFloat(formData.ticketSizeMax) || 0 : 0,
        portfolio_companies: formData.portfolioCompanies ? parseInt(formData.portfolioCompanies) || 0 : 0,
        contact_info: formData.contactInfo,
        investment_thesis: formData.investmentCriteria || '',
      };

      // Create venture capital record
      const { error } = await supabase
        .from('venture_capitals')
        .insert(ventureData);

      if (error) {
        console.error('Error creating venture capital:', error);
        toast.error('Failed to create venture capital profile');
        return;
      }

      // Update profile account_type with email and display_name
      await supabase
        .from('profiles')
        .update({ 
          email: user.email || '',
          display_name: formData.firmName || user.email?.split('@')[0] || 'VC',
          account_type: 'venture',
          setup_completed: true 
        })
        .eq('id', profile.id);

      toast.success('Venture capital profile created successfully!');
      onComplete(formData);
    } catch (error) {
      console.error('Error:', error);
      toast.error('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="firmName">Firm Name *</Label>
              <Input
                id="firmName"
                placeholder="e.g., Andreessen Horowitz, Sequoia Capital"
                value={formData.firmName}
                onChange={(e) => updateFormData('firmName', e.target.value)}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="aum">Assets Under Management *</Label>
              <Select value={formData.aum} onValueChange={(value) => updateFormData('aum', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select AUM range" />
                </SelectTrigger>
                <SelectContent>
                  {aumRanges.map((range) => (
                    <SelectItem key={range} value={range}>
                      {range}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label>Investment Focus Areas *</Label>
                <p className="text-sm text-muted-foreground mb-3">
                  Select the areas your firm focuses on
                </p>
                <div className="flex gap-2 mb-3">
                  <Select value={newFocus} onValueChange={setNewFocus}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select focus area" />
                    </SelectTrigger>
                    <SelectContent>
                      {focusAreas.map((area) => (
                        <SelectItem key={area} value={area}>
                          {area}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button onClick={addFocus} variant="outline">Add</Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.investmentFocus.map((focus, index) => (
                    <Badge
                      key={index}
                      variant="secondary"
                      className="cursor-pointer"
                      onClick={() => removeFocus(index)}
                    >
                      {focus} ×
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center space-y-2 mb-6">
              <DollarSign className="h-8 w-8 text-primary mx-auto" />
              <h3 className="text-lg font-semibold">Investment Details</h3>
              <p className="text-muted-foreground text-sm">
                Share your investment parameters and preferences
              </p>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label>Investment Stage Preferences *</Label>
                <p className="text-sm text-muted-foreground mb-3">
                  Select the stages you typically invest in
                </p>
                <div className="flex gap-2 mb-3">
                  <Select value={newStage} onValueChange={setNewStage}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select stage" />
                    </SelectTrigger>
                    <SelectContent>
                      {stageOptions.map((stage) => (
                        <SelectItem key={stage} value={stage}>
                          {stage}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button onClick={addStage} variant="outline">Add</Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.stagePreference.map((stage, index) => (
                    <Badge
                      key={index}
                      variant="secondary"
                      className="cursor-pointer"
                      onClick={() => removeStage(index)}
                    >
                      {stage} ×
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="ticketSizeMin">Min Ticket Size ($)</Label>
                <Input
                  id="ticketSizeMin"
                  type="number"
                  placeholder="e.g., 100000"
                  value={formData.ticketSizeMin}
                  onChange={(e) => updateFormData('ticketSizeMin', e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="ticketSizeMax">Max Ticket Size ($)</Label>
                <Input
                  id="ticketSizeMax"
                  type="number"
                  placeholder="e.g., 5000000"
                  value={formData.ticketSizeMax}
                  onChange={(e) => updateFormData('ticketSizeMax', e.target.value)}
                />
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center space-y-2 mb-6">
              <Target className="h-8 w-8 text-primary mx-auto" />
              <h3 className="text-lg font-semibold">Portfolio & Strategy</h3>
              <p className="text-muted-foreground text-sm">
                Tell us about your portfolio and investment approach
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="portfolioCompanies">Current Portfolio Size</Label>
              <Input
                id="portfolioCompanies"
                type="number"
                placeholder="e.g., 25"
                value={formData.portfolioCompanies}
                onChange={(e) => updateFormData('portfolioCompanies', e.target.value)}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="investmentCriteria">Investment Criteria</Label>
              <Textarea
                id="investmentCriteria"
                placeholder="Describe what you look for in potential investments..."
                value={formData.investmentCriteria}
                onChange={(e) => updateFormData('investmentCriteria', e.target.value)}
                rows={4}
              />
            </div>
            
            <div className="space-y-4">
              <h4 className="font-medium">Contact Information</h4>
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="contactEmail">Business Email *</Label>
                  <Input
                    id="contactEmail"
                    type="email"
                    placeholder="partner@vcfirm.com"
                    value={formData.contactInfo.email}
                    onChange={(e) => updateFormData('contactInfo.email', e.target.value)}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="contactPhone">Phone Number</Label>
                  <Input
                    id="contactPhone"
                    placeholder="(555) 123-4567"
                    value={formData.contactInfo.phone}
                    onChange={(e) => updateFormData('contactInfo.phone', e.target.value)}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="contactWebsite">Firm Website</Label>
                  <Input
                    id="contactWebsite"
                    placeholder="https://www.yourfirm.com"
                    value={formData.contactInfo.website}
                    onChange={(e) => updateFormData('contactInfo.website', e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center space-y-2">
              <TrendingUp className="h-12 w-12 text-primary mx-auto" />
              <h3 className="text-lg font-semibold">Review Your VC Profile</h3>
              <p className="text-muted-foreground">
                Please review all information before creating your profile
              </p>
            </div>
            
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  {formData.firmName}
                </CardTitle>
                <CardDescription>AUM: {formData.aum}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {formData.investmentFocus.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2">Investment Focus</h4>
                    <div className="flex flex-wrap gap-1">
                      {formData.investmentFocus.map((focus, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {focus}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                
                {formData.stagePreference.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2">Stage Preferences</h4>
                    <div className="flex flex-wrap gap-1">
                      {formData.stagePreference.map((stage, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {stage}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium">Min Ticket:</span>
                    <p className="text-muted-foreground">${formData.ticketSizeMin?.toLocaleString()}</p>
                  </div>
                  <div>
                    <span className="font-medium">Max Ticket:</span>
                    <p className="text-muted-foreground">${formData.ticketSizeMax?.toLocaleString()}</p>
                  </div>
                  <div>
                    <span className="font-medium">Portfolio Size:</span>
                    <p className="text-muted-foreground">{formData.portfolioCompanies} companies</p>
                  </div>
                  <div>
                    <span className="font-medium">Email:</span>
                    <p className="text-muted-foreground">{formData.contactInfo.email}</p>
                  </div>
                </div>
                
                {formData.investmentCriteria && (
                  <div>
                    <h4 className="font-medium mb-1">Investment Criteria</h4>
                    <p className="text-sm text-muted-foreground">{formData.investmentCriteria}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="container mx-auto max-w-2xl space-y-8">
        {/* Header */}
        <div className="text-center space-y-4 pt-8">
          <h1 className="text-3xl font-bold text-foreground">
            Venture Capital Profile Setup
          </h1>
          <p className="text-muted-foreground">
            Let's create your venture capital profile to get started
          </p>
        </div>

        {/* Progress */}
        <div className="space-y-4">
          <Progress value={progress} className="w-full" />
          <div className="flex justify-between text-sm text-muted-foreground">
            {steps.map((step, index) => (
              <div key={step.id} className={`flex items-center gap-1 ${currentStep >= step.id ? 'text-primary' : ''}`}>
                <step.icon className="h-4 w-4" />
                <span className="hidden sm:inline">{step.title}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <Card>
          <CardContent className="pt-6">
            {renderStepContent()}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between">
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={currentStep === 1 ? onBack : prevStep}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              {currentStep === 1 ? (isCompletion ? 'Back' : 'Back to Roles') : 'Previous'}
            </Button>
            {isCompletion && (
              <Button 
                variant="ghost" 
                onClick={() => window.location.href = '/profile'}
                className="flex items-center gap-2"
              >
                Return to Profile
              </Button>
            )}
          </div>
          
          {currentStep < steps.length ? (
            <Button onClick={nextStep} className="flex items-center gap-2">
              Next
              <ArrowRight className="h-4 w-4" />
            </Button>
          ) : (
            <Button 
              onClick={handleSubmit} 
              disabled={loading || !formData.firmName || !formData.aum || formData.investmentFocus.length === 0}
              className="flex items-center gap-2"
            >
              {loading ? 'Creating Profile...' : 'Complete Setup'}
              <CheckCircle className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};